import { BaseLkApiAuth } from '@/api/BaseApiAuth'
import { AxiosRequestConfig } from 'axios'
import { Publication } from '@/typings/api/Publication'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Recomendation {}

interface RecomendationCollection extends Recomendation {}

export type RecomendationAggregatedProfileType = 'adm' | 'reg' | 'sch' | 'avg';

export interface RecomendationAggregatedGenerateParams {
  school_ids: string[];
}

export interface RecomendationAggregatedGenerateResponse {
  id: number;
}

export type RecomendationAggregatedStatus = 'pending' | 'in_progress' | 'done' | 'failed';

export interface RecomendationAggregatedGeneration {
  id: number;
  publication_id: Publication['id'];
  user_id: number;
  status: RecomendationAggregatedStatus;
  profile_type: RecomendationAggregatedProfileType;
  file_path?: string;
  file_name?: string;
  error_message: string;
  created_at: string;
  finished_at: string;
  school_ids: string[];
}

export interface RpshV3MassGenerationResponse {
  total: number;
  started: number;
  school_logins: string[];
  enqueue_errors: string[];
}

export interface RpshV3MassGenerationItem {
  school_id: string;
  school_login: string;
  status: 'not_requested_yet' | 'in_progress' | 'not_ready' | 'done' | 'error';
  updated_at: string;
  error_message: string | null;
}

export interface RpshV3MassGenerationStatus {
  total: number;
  done: number;
  progress: number;
  statuses: Record<string, number>;
  items: RpshV3MassGenerationItem[];
}

export class LkRecomendationApi extends BaseLkApiAuth<
  Recomendation,
  RecomendationCollection
> {
  protected readonly endpoint = 'recommendation';

  public getDownloadUrl(publicationId: Publication['id']) {
    const path = `publication/${publicationId}/download`
    const [url] = this.getUrl(path)
    return url
  }

  public generateOne(
    publicationId: Publication['id'],
    data: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/generate/one`
    const [url] = this.getUrl(path)
    return this.request('POST', url, { data, ...config })
  }

  public generate(
    publicationId: Publication['id'],
    rawParams: Record<string, unknown> = {},
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/generate`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request('POST', url, { params, ...config })
  }

  public getRecomendationStatus(
    publicationId: Publication['id'],
    rawParams: Record<string, unknown> = {},
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/status`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request('GET', url, { params, ...config })
  }

  public getRpshV3IndexMedianasSchoolLoginsTemplateUrl() {
    const path = 'rpsh-v3/index-medianas/school-logins-template'
    const [url] = this.getUrl(path)
    return url
  }

  public getRpshV3IndexMedianasUrl(algorithm?: string) {
    const path = 'rpsh-v3/index-medianas'
    const [url] = this.getUrl(path)
    return algorithm
      ? `${url}?algorithm=${encodeURIComponent(algorithm)}`
      : url
  }

  public uploadRpshV3IndexMedianas(
    publicationId: Publication['id'],
    file: File,
    forced = false,
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/rpsh-v3/index-medianas/upload`
    const [url] = this.getUrl(path)
    const data = new FormData()
    data.append('file', file)
    return this.request<never, RecomendationAggregatedGenerateResponse>(
      'POST',
      url,
      { params: { forced }, data, ...config },
    )
  }

  public uploadRpshV3MassGeneration(
    publicationId: Publication['id'],
    file: File,
    forced = false,
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/rpsh-v3/generate/mass`
    const [url] = this.getUrl(path)
    const data = new FormData()
    data.append('file', file)
    return this.request<never, RpshV3MassGenerationResponse>(
      'POST',
      url,
      { params: { forced }, data, ...config },
    )
  }

  public getRpshV3MassGenerationStatus(
    publicationId: Publication['id'],
    rawParams: Record<string, unknown> = {},
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/rpsh-v3/generate/mass/status`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<never, RpshV3MassGenerationStatus>('GET', url, {
      params,
      paramsSerializer: (values) => {
        const searchParams = new URLSearchParams()
        Object.entries(values).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, String(item)))
            return
          }
          searchParams.append(key, String(value))
        })
        return searchParams.toString()
      },
      ...config,
    })
  }

  public getRpshV3MassGenerationDownloadUrl(
    publicationId: Publication['id'],
    schoolLogin: string,
  ) {
    const path = `publication/${publicationId}/rpsh-v3/generate/mass/${schoolLogin}/download`
    const [url] = this.getUrl(path)
    return url
  }

  public generateAggregated(
    publicationId: Publication['id'],
    schoolIds: string[],
    profileType: RecomendationAggregatedProfileType = 'sch',
    forced = false,
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/generate/aggregated`
    const [url, params] = this.getUrl(path, { profile_type: profileType, forced })
    return this.request<never, RecomendationAggregatedGenerateResponse>(
      'POST',
      url,
      { params, data: { school_ids: schoolIds }, ...config },
    )
  }

  public generateAggregatedFromCsv(
    publicationId: Publication['id'],
    profileType: RecomendationAggregatedProfileType,
    file: File,
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/generate/aggregated`
    const [url] = this.getUrl(path)
    const data = new FormData()
    data.append('file', file)
    return this.request<never, RecomendationAggregatedGenerateResponse>(
      'POST',
      url,
      { params: { profile_type: profileType, forced: false }, data, ...config },
    )
  }

  public getAggregatedGenerationStatus(
    publicationId: Publication['id'],
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/generate/aggregated/status`
    const [url] = this.getUrl(path)
    return this.request<never, RecomendationAggregatedGeneration[]>(
      'GET',
      url,
      config,
    )
  }

  public downloadAggregatedGeneration(
    publicationId: Publication['id'],
    recommendationAggregatedId: RecomendationAggregatedGeneration['id'],
    config?: AxiosRequestConfig,
  ) {
    const url = this.getAggregatedGenerationDownloadUrl(
      publicationId,
      recommendationAggregatedId,
    )
    return this.request<never, Blob>(
      'GET',
      url,
      { responseType: 'blob', ...config },
    )
  }

  public getAggregatedGenerationDownloadUrl(
    publicationId: Publication['id'],
    recommendationAggregatedId: RecomendationAggregatedGeneration['id'],
  ) {
    const path = `publication/${publicationId}/generate/aggregated/${recommendationAggregatedId}/download`
    const [url] = this.getUrl(path)
    return url
  }

  public deleteAggregatedGeneration(
    publicationId: Publication['id'],
    recommendationAggregatedId: RecomendationAggregatedGeneration['id'],
    config?: AxiosRequestConfig,
  ) {
    const path = `publication/${publicationId}/generate/aggregated/${recommendationAggregatedId}`
    const [url] = this.getUrl(path)
    return this.request<never, void>('DELETE', url, config)
  }
}
